'use client';
import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Dna, Star, Palette, Package, Share2, Rocket, Map, FileText, TrendingUp } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


export default function WhatYouReceiveSection() {
  const { t } = useLanguage();
  const wr = t?.whatYouReceive;

  const cards = [
    {
      icon: Dna,
      title: wr?.card1Title ?? 'Dream DNA Analysis',
      desc: wr?.card1Desc ?? 'A deep analysis of your dream\'s strengths, opportunities, risks, and full business potential.',
      color: 'text-primary',
      bg: 'bg-primary/10',
      border: 'border-primary/20',
    },
    {
      icon: Star,
      title: wr?.card2Title ?? 'Dream Score™',
      desc: wr?.card2Desc ?? 'A scored evaluation of business potential, startup cost, difficulty, scalability, and AI leverage.',
      color: 'text-yellow-400',
      bg: 'bg-yellow-400/10',
      border: 'border-yellow-400/20',
    },
    {
      icon: Palette,
      title: wr?.card3Title ?? 'Brand Kit',
      desc: wr?.card3Desc ?? 'Name ideas, color palettes, slogans, logo concepts, and brand personality.',
      color: 'text-accent',
      bg: 'bg-accent/10',
      border: 'border-accent/20',
    },
    {
      icon: Package,
      title: wr?.card4Title ?? 'Product Strategy',
      desc: wr?.card4Desc ?? 'Product line ideas, pricing strategy, packaging concepts, and revenue model.',
      color: 'text-mint',
      bg: 'bg-mint/10',
      border: 'border-mint/20',
    },
    {
      icon: Share2,
      title: wr?.card5Title ?? 'Content Plan',
      desc: wr?.card5Desc ?? 'Post ideas, reel concepts, captions, and a 7-day content calendar.',
      color: 'text-pink-400',
      bg: 'bg-pink-400/10',
      border: 'border-pink-400/20',
    },
    {
      icon: Rocket,
      title: wr?.card6Title ?? 'Launch Roadmap',
      desc: wr?.card6Desc ?? 'A 90-day roadmap with milestones, quick wins, and immediate next actions.',
      color: 'text-orange-400',
      bg: 'bg-orange-400/10',
      border: 'border-orange-400/20',
    },
    {
      icon: Map,
      title: wr?.card7Title ?? 'Visual Concepts',
      desc: wr?.card7Desc ?? 'AI-generated visual inspiration for your brand, products, and marketing materials.',
      color: 'text-purple-400',
      bg: 'bg-purple-400/10',
      border: 'border-purple-400/20',
    },
    {
      icon: FileText,
      title: wr?.card8Title ?? 'Project Files',
      desc: wr?.card8Desc ?? 'All generated assets, images, PDFs, and documents saved in your project workspace.',
      color: 'text-blue-400',
      bg: 'bg-blue-400/10',
      border: 'border-blue-400/20',
    },
    {
      icon: TrendingUp,
      title: wr?.card9Title ?? 'Progress Tracking',
      desc: wr?.card9Desc ?? 'Task checklists, milestone tracking, and project completion status — all in one place.',
      color: 'text-green-400',
      bg: 'bg-green-400/10',
      border: 'border-green-400/20',
    },
  ];

  return (
    <section className="py-20 px-6 lg:px-10 relative overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-700 uppercase tracking-widest text-primary mb-3 px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
            {wr?.sectionLabel ?? 'What You Will Receive'}
          </span>
          <h2 className="text-3xl md:text-4xl font-800 text-foreground mb-4">
            {wr?.sectionTitle ?? 'Everything Included in Your'}{' '}
            <span className="gradient-text">{wr?.sectionTitleHighlight ?? 'Dream DNA™ Analysis'}</span>
          </h2>
          <p className="text-base text-muted-foreground max-w-2xl mx-auto">
            {wr?.sectionSubtitle ?? 'Every dream you enter generates a complete set of strategic and creative deliverables — powered by Dream DNA Studio™.'}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards?.map((card, i) => {
            const Icon = card?.icon;
            return (
              <div
                key={i}
                className="glass-card rounded-2xl p-6 border border-border hover:border-primary/30 transition-all duration-200 group"
              >
                <div className={`w-11 h-11 rounded-xl ${card?.bg} border ${card?.border} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200`}>
                  <Icon size={20} className={card?.color} />
                </div>
                <h3 className="text-base font-700 text-foreground mb-2">{card?.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{card?.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
